import { createSelector } from "@reduxjs/toolkit";
// Types
import Reducers from "types/Reducers";
import PageTypes from "types/PageTypes";
import QuizTypes from "types/QuizTypes";
// Model
import { RootState } from "store";
import IQuiz from "models/Quiz";
import IAnswer, { IAnswerResult } from "models/Answer";
import IBucket, { IBucketResult } from "models/Bucket";
import IPostQuizPage from "models/PostQuizPage";
import IQuestion from "models/Question";
// Selectors
import { selectAnswersOptions, selectQuestion, selectQuestions } from "store/quiz/quizSelectors";
import { selectPages } from "store/pages/pagesSelectors";
import QuestionTypes from "types/QuestionTypes";

export const selectPlayer = (state:RootState) => state[Reducers.Player].player;
export const selectExistingPlayer = (state:RootState) => state[Reducers.Player].existingPlayer;
export const selectResult = (state:RootState) => state[Reducers.Player].result;
export const selectResultUploadedAt = (state:RootState) => state[Reducers.Player].resultUploadedAt;

export const selectAnswers = (state:RootState) => state[Reducers.Player].answers;
export const selectBuckets = (state:RootState) => state[Reducers.Player].buckets;

export const selectEntryTime = (state:RootState) => state[Reducers.Player].entryTime;
export const selectAnswerTime = (state:RootState) => state[Reducers.Player].answerTime;

export const selectQuestionIndex = (state:RootState) => state[Reducers.Player].question.index;
export const selectQuestionReady = (state:RootState) => state[Reducers.Player].question.ready;
export const selectQuestionStart = (state:RootState) => state[Reducers.Player].question.start;
export const selectQuestionEnd = (state:RootState) => state[Reducers.Player].question.end;
export const selectQuestionAnswerPicked = (state:RootState) => state[Reducers.Player].question.answerPicked;
export const selectQuestionAnswerRefs = (state:RootState) => state[Reducers.Player].question.answerRefs;
export const selectIPLocationInfo = (state:RootState) => state[Reducers.Player].ipLocationInfo;
export const selectPreviewPageMode = (state:RootState) => state[Reducers.Player].previewPageMode;

export const selectLeaderboard = createSelector(
  [ selectResult, selectPreviewPageMode ],
  (result:any, previewPageMode) => {
    if ( previewPageMode ) return previewResults;
    if ( !result ) return [];
    if ( !result.results || result.results.length === 0 ) return [];
    return [...result.results].sort((a:any, b:any) => b.score - a.score);
  }
);

export const selectTopBucket = createSelector(
  [ selectResult ],
  (result:any) => {
    if ( !result || !result.currentUserResult ) return null;
    const { buckets } = result.currentUserResult;
    if ( !buckets || !buckets.length ) return null;
    return [...buckets].reduce((acc:IBucketResult, cur:IBucketResult) => {
      if (cur.score > acc.score) return cur;
      return acc;
    });
  }
);

export const selectPostQuizPageByTopBucket = createSelector(
  [
    // selectQuiz,
    // ToDO: For some reason selector throw an error with `circular dependency issue`
    (state:RootState) => state.quiz.quiz,
    selectTopBucket
  ],
  (quiz:IQuiz | null, bucket:IBucketResult | null) => {
    if ( !quiz || !bucket ) return undefined;
    if ( !quiz.postQuizPages ) return undefined;
    return quiz.postQuizPages.find((postQuizPage:IPostQuizPage) => postQuizPage.condition.value === bucket.ref)
  }
);

export const selectMaxScore = createSelector(
  [
    selectPages,
    selectQuestions,
  ],
  (pages, questions) => {
    const questionsPages = pages.filter(page => page.type === PageTypes.Question);
    const simpleQuestions = questions.filter(question => questionsPages.some(page => page.refs.includes(question.ref)));
    
    return simpleQuestions.reduce((acc:number, cur:IQuestion) => {
      if (cur.type === QuestionTypes.TopList) {
        const maxTopListScore = cur.config?.items?.reduce((innerAcc, innerCur) => {
          return innerAcc + (innerCur.scoring.points || 0);
        }, 0);
        return acc + (maxTopListScore || 0);
      }
      return acc + (cur.score || 0);
    }, 0);
  }
)

export const selectTotalScore = createSelector(
  [ selectAnswers ],
  (answers:IAnswerResult[]) => {
    if ( !answers.length ) return 0;
    return answers.reduce((acc:number, cur:IAnswerResult) => {
      if ( cur.correct !== false ) acc = acc + (cur.score || 0);
      return acc;
    }, 0);
  }
);

export const selectExpertCurrectScore = createSelector(
  [
    selectAnswers,
    selectQuestions
  ],
  (answers, questions) => {
    return answers.reduce((acc:number, cur:IAnswerResult) => {
      const question = questions.find(q => q.ref === cur.question.ref);
      if (!question) return acc;
      const expertAnswerRef = question.config?.referenceAnswer?.input.choice.ref;
      const correctAnswerRef = question.answers.find((a:any) => a.correct)?.ref;

      if (expertAnswerRef === correctAnswerRef) {
        acc += question.config?.referenceAnswer?.score || 0;
      }
      return acc;
    }, 0);
  }
)

export const selectExpertTotalScore = createSelector(
  [selectQuestions],
  (questions:IQuestion[]) => {
    return questions.reduce((acc:number, cur:IQuestion) => {
      const correctAnswer = cur.answers.find((a:IAnswer) => a.correct);
      const isExpertCorrect = correctAnswer?.ref === cur.config?.referenceAnswer?.input.choice.ref;
      if (cur.config?.referenceAnswer && isExpertCorrect) {
        acc += cur.config.referenceAnswer.score || 0;
      }
      return acc;
    }, 0);
  }
)

export const selectIsAnyAnswerCorrect = createSelector(
  [
    selectQuestion
  ],
  (question) => {
    if ( !question || !question.answers ) return false;

    return question.answers.every((answer:IAnswer) => !answer.correct);
  }
);

export const selectIsAnswerRefInRefs = createSelector(
  [
    selectQuestionAnswerRefs,
    (_, props:{ answerRef:string }) => props
  ],
  (questionAnswerRefs:string[], { answerRef }) => questionAnswerRefs.includes(answerRef)
);

export const selectAnswersByAnswerRefs = createSelector(
  [
    selectQuestion,
    selectQuestionAnswerRefs,
    selectAnswerTime
  ],
  (question:IQuestion, questionAnswerRefs:string[], time) => {
    if ( !question ) return [];
    if ( !questionAnswerRefs.length ) return [];

    const filteredAnswers = question.answers.filter((answer:IAnswer) => questionAnswerRefs.includes(answer.ref));

    return filteredAnswers.map(({ ref, title }:IAnswer) => ({
      type: question.type,
      time,
      question: {
        ref: question.ref,
        title: question.title
      },
      input: {
        choice: { ref, title }
      }
    }));
  }
);

export const selectBucketsByAnswerRefs = createSelector(
  [
    // selectQuiz,
    // ToDO: For some reason selector throw an error with `circular dependency issue`
    (state:RootState) => state.quiz.quiz,
    selectQuestion,
    selectQuestionAnswerRefs
  ],
  (quiz:IQuiz | null, question:IQuestion, questionAnswerRefs:string[]) => {
    if ( !quiz || quiz.type !== QuizTypes.Personality ) return [];
    if ( !questionAnswerRefs.length ) return [];
    if ( !question || !question.answers ) return [];

    const filteredAnswers = question.answers.filter((answer:IAnswer) => questionAnswerRefs.includes(answer.ref)) || [];

    const buckets:{ [key:string]:IBucketResult } = quiz.buckets.reduce((acc:{ [key:string]:IBucketResult }, cur:IBucket) => {
      acc[cur.ref] = {...cur, score: 0};
      return acc;
    }, {});

    for ( let answer of filteredAnswers ){
      for ( let bucket of answer.bucketScores ){
        if ( buckets[bucket.ref] ){
          const score = buckets[bucket.ref]['score'];
          buckets[bucket.ref]['score'] = score + (bucket.score || 0);
        }
      }
    }

    return buckets;
  }
);

export const selectFilteredAnswersOptions = createSelector(
  [
    selectAnswersOptions,
    selectAnswers,
    selectQuestion
  ],
  (answersOptions, answers, question) => {
    if (!answersOptions) return [];
    return answersOptions.filter(answerOption => !answers.some(answer => (answer.input.choice?.ref === answerOption.ref && answer.question.ref !== question.ref)));
  }
);

interface IAnswerMap {
  [key: string]: {
    userCorrect: boolean | undefined;
    expertCorrect: boolean | undefined;
  };
}

export const selectUserAndExpertAnswers = createSelector(
  [
    selectAnswers,
    selectQuestions
  ],
  (answers:IAnswerResult[], questions:IQuestion[]) => {
    return answers.reduce((acc:IAnswerMap, answer:IAnswerResult) => {
      const question = questions.find(q => q.ref === answer.question.ref);
      if (!question) return acc;
      const expertAnswerRef = question.config?.referenceAnswer?.input.choice.ref;
      const correctAnswerRef = question.answers.find(a => a.correct)?.ref;

      return {
        ...acc,
        [question.ref]: {
          userCorrect: answer.correct,
          expertCorrect: expertAnswerRef ? expertAnswerRef === correctAnswerRef : undefined
        }
      }
    }, {});
  }
)

const previewResults = new Array(10).fill(null).map((_, index) => ({
  username: `Player ${index + 1}`,
  score: Math.floor(5000 - (index * 100)),
  position: index + 1
}));